import Keycloak from 'keycloak-js';
import { Injectable } from '@angular/core';
import { keycloakConfig } from '../../../env/env';
import { HttpClient } from '@angular/common/http';
import { AuthLoginRequest, AuthRegisterRequest } from "../../data/http/requests/auth.request";
import { ApiResponse } from '../../data/http/responses/apiresponse.response';
import { catchError, Observable, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private keycloak: Keycloak.KeycloakInstance | undefined;
  private initialized: boolean = false;
  private refreshIntervalId: any;
  private activityEvents: string[] = ['click', 'mousemove', 'keydown', 'touchstart'];
  private lastRefreshAt: number = 0;
  private activityListener = () => {
    const now = Date.now();
    // avoid too frequent refreshes (at most once per minute)
    if (now - this.lastRefreshAt < 60 * 1000) return;
    this.keycloak?.updateToken(60)
      .then(() => { this.lastRefreshAt = now; })
      .catch(() => {
        // ignore: periodic/interaction-triggered refresh may fail if session ended
      });
  };

  constructor(private http: HttpClient) { }

  init(): Promise<boolean> {
    this.keycloak = new Keycloak({
      url: keycloakConfig.baseUrl,
      realm: keycloakConfig.realm,
      clientId: keycloakConfig.clientId
    });

    // DEV helper: expose instance for debugging in console
    try {
      (window as any).keycloak = this.keycloak;
    } catch (e) { }

    // Disable Keycloak's checkLoginIframe to avoid adding an iframe that uses postMessage
    // which can trigger costly 'message' handlers in some environments.
    // If you need iframe-based session check, set this back to true.
    return this.keycloak.init({ onLoad: 'login-required', checkLoginIframe: false })
      .then((authenticated) => {
        this.initialized = !!authenticated;

        if (authenticated) {
          // Try an immediate token update to ensure we have a fresh token
          try {
            this.keycloak?.updateToken(70).catch(() => {
              // ignore initial refresh failure; onTokenExpired will handle subsequent cases
            });
          } catch (e) {
            // ignore
          }

          // When token expires, attempt a refresh and log out on failure
          (this.keycloak as any).onTokenExpired = () => {
            this.keycloak?.updateToken(30).catch(() => {
              console.warn('Token refresh failed on expiry — logging out');
              this.logout();
            });
          };

          // Periodically try to refresh token when nearing expiry (every minute)
          this.refreshIntervalId = setInterval(() => {
            this.keycloak?.updateToken(60).catch((err) => {
              console.warn('Periodic token refresh failed', err);
            });
          }, 60 * 1000);

          // Attach activity listeners so we refresh on user action
          try {
            this.attachActivityListeners();
          } catch (e) {
            // ignore if document not available
          }
        }

        return authenticated;
      });
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  login() {
    this.keycloak?.login();
  }

  logout() {
    // clear any refresh interval
    try {
      if (this.refreshIntervalId) clearInterval(this.refreshIntervalId);
    } catch (e) { }
    try {
      this.detachActivityListeners();
    } catch (e) { }
    this.keycloak?.logout();
  }

  private attachActivityListeners() {
    if (typeof document === 'undefined') return;
    this.activityEvents.forEach(ev => document.addEventListener(ev, this.activityListener));
  }

  private detachActivityListeners() {
    if (typeof document === 'undefined') return;
    this.activityEvents.forEach(ev => document.removeEventListener(ev, this.activityListener));
  }

  getToken(): string | undefined {
    return this.keycloak?.token;
  }

  getTokenParsed(): any | undefined {
    return this.keycloak?.tokenParsed as any | undefined;
  }

  getUsername(): string | undefined {
    return this.getTokenParsed() ? (this.getTokenParsed() as any).preferred_username : undefined;
  }

  getRealmRoles(): string[] {
    const parsed = this.getTokenParsed();
    if (parsed && parsed.realm_access && Array.isArray(parsed.realm_access.roles)) {
      return parsed.realm_access.roles.map((r: string) => r.toString());
    }
    return [];
  }

  getUserInfo(): any {
    const parsedToken = this.getTokenParsed();
    if (parsedToken) {
      return {
        username: parsedToken.preferred_username,
        email: parsedToken.email,
        roles: parsedToken.realm_access?.roles || []
      };
    }
    return null;
  }

  getGroupsFromToken(): string[] {
    const parsedToken = this.getTokenParsed();
    if (parsedToken && parsedToken['groups']) {
      return parsedToken['groups'];
    }
    return [];
  }

  getUserRoles(): string[] {
    const parsedToken = this.getTokenParsed();
    if (parsedToken && parsedToken.realm_access && parsedToken.realm_access.roles) {
      return parsedToken.realm_access.roles;
    }
    return [];
  }

  hasRole(role: string): boolean {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }

  canActivate(): Promise<boolean> {
    return this.init().then(authenticated => {
      if (!authenticated) {
        console.warn('User not authenticated');
        return false;
      }
      return true;
    });
  }

  protected handleError(error: any): Observable<never> {
    console.error('An error occurred in AuthService:', error);
    return throwError(() => new Error('An error occurred during the request. Please try again later.'));
  }
}